<template lang="pug">
#event-edit(v-cloak)
  .box
    .box-header.with-border.register-header
      .title
        span イベント情報修正
    .box-body.register-body
      // 日付・時間
      FormEventDatetimePicker(v-model="eventDatetime" :date.sync="eventDate" :startTime.sync="eventTimeFrom" :endTime.sync="eventTimeTo"
        key-name="日付・時間" name="event-date" placeholder="日付・時間" :validation-rules="{ required: true }" required
      )

      // 公開範囲
      FormInputEventTargetType(v-model="targetType")
      .register-row(v-show="1 <= targetType")
        .register-group
          .register-key
            span.key-name 公開先
            span.validation-label 必須
          .register-value.flex-wrap.send-targets
            .loading(v-if="isBusy")
              b-spinner.spinner
              span 公開先読込中...
            button.btn.target-select-btn(v-else @click="showTargetSelectModal()") {{ headerTitle }}選択
            .event-target.d-flex(v-for="target in _targetEventTargetSelectModalModel")
              span.target-name.align-self-center {{ target.name }}
              b-badge.delete-btn.align-self-center(variant="secondary" @click="deleteTarget(target)") X
          .error-messages(v-if="errors.has('event-targets')")
            div(v-for="error in errors.collect('event-targets')")
              label.error-message {{ error }}

      // 開催方法
      FormInputEventHoldType(v-model="holdType" :active="2 == planType")
        template(v-slot:question)
          HelpIcon(popoverId="form-input-event-hold-type-help" title="開催方法について")
            template(v-slot:text)
              span オンライン開催の場合、イベント当日にライブ配信することが出来ます。
              span ご家族は自宅からスマートフォンで視聴出来ます。
              br
              span ※ ベーシックプランで使用可能
      // 参加申し込み設定・参加申込み締切日、ベーシックプランのみ使用可能な機能
      FormCheck(v-model="needReply" key-name="参加申込設定" name="event-need-reply" label="参加申込み有り" 
        :active="2 == planType" :disabled="planType != 2 || needReplyDisabled == true")
        template(v-slot:question)
          HelpIcon(popoverId="form-input-event-need-reply-help" title="参加申込みについて")
            template(v-slot:text)
              span ご家族がイベントの参加申込みをすることが出来ます。
              span 施設側では参加申込み状況を確認出来ます。
              br
              span オンライン開催の場合は「参加申込み有り」になります。
              br
              span ※ ベーシックプランで使用可能
      FormDatePicker(v-show="needReply == true" v-model="replyDeadline" :date.sync="replyDeadlineDate"
        key-name="参加申込み締切日" name="reply-deadline" placeholder="参加申込み締切日" required
      )

      // 場所・タイトル・内容・画像ファイル
      FormInput(v-model="place" key-name="場所" name="place" type="text" placeholder="場所" :maxlength="100"
        :validation-rules="{ required: true, max: 100 }" required)
      FormInput(v-model="title" key-name="タイトル" name="title" type="text" placeholder="タイトル" :maxlength="100"
        :validation-rules="{ required: true, max: 100 }" required)
      FormTextarea(v-model="description" key-name="内容" name="description" placeholder="内容" :maxlength="1000" :rows="7"
        :validation-rules="{ required: true, max: 1000 }" :styles="{ maxWidth: '600px', width: '600px' }" required)
      FormMultiFile(:registerImageFilesLimit="_registerFilesLimit" :enablePreviewStatus="_enablePreviewStatus"
        @inputFiles="inputFiles" @onTempFilePreview="onTempFilePreview")
      .register-row
        DraggableFiles(v-model="files" @delete="onFileDelete" :item-width="'468px'")
      FormFile(v-model="imageFile" key-name="イメージ画像ファイル" comment="※ファイル選択しない場合は初期画像が表示されます" name="image-file" :file-name.sync="imageName"
        mimetype="image/*" width="450px" :validationRules="{}" clip)
        template(v-slot:question)
          HelpIcon(:title="'選択可能なファイルについて'" :text="'jpegもしくはpngを選択可能です。'")

      // プレビュー
      .register-row
        .register-group
          .register-key
            span.key-name プレビュー
          .register-value
            EventPreview(:date="eventDatetime" :timeFrom="eventTimeFrom" :timeTo="eventTimeTo" :place="place" :title="title"
              :description="description" :imageUrl="imageUrl" :holdType="holdType" :needReply="needReply" :replyDeadline="replyDeadline"
              :files="files")

      // 更新失敗等の全体エラーメッセージ
      .register-row(v-show="errorMessage")
        .register-group
          .register-value
            .error-message {{ errorMessage }}

    .box-footer.with-border.register-footer
      ValidationAlert(v-model="showValidationAlert")
      .buttons
        button.btn.update-btn(@click="showConfirmUpdateModal()" type="button") 更新
        button.btn.cancel-btn(@click="gotoEventList()" type="button") キャンセル
  img(ref="defaultImage" :src="defaultImage" style="display: none;")

  OkCancelModal(v-model="showUpdateModal" title="イベント情報を更新しますか？" @ok="updateEvent")
  OkModal(v-model="showUpdateSuccessModal" title="更新に成功しました。" @ok="gotoEventList")
  EventTargetSelectModal(v-model="isTargetSelectModal" :selectOptions="tempEventTargetSelectModalModel" :headerTitle="headerTitle" @ok="updateTarget")
  EventFilePreviewModal(ref="filePreviewModal")

  PageLoading(:isBusy="isBusy" :message="loadingMessage")
  ProgressLoading(v-model="currentUploadCount" :max="uploadCount" :isBusy="progressLoading" :message="progressMessage")
</template>

<script lang="ts">
import { Component, Inject, Watch, Ref, Vue } from "vue-property-decorator";
import { userStore } from "@/store/store";
import { needRefreshesStore } from "@/store/store";
import { EventListState } from "@/store/NeedRefreshes";
import uuid from "uuid";

import Setting from "../../../../public/setting.json";
import CustomValidate from "@/commons/utils/CustomValidate";
import { PcUrl } from "@/commons/constants/PcUrl";
import EventsService, { EventGetModel, EventHoldType, EventTargetType, EventPatchModel, FileUrl } from "@/commons/services/Events";
import ResidentGroupsService, { ResidentGroupsGetModel } from "@/commons/services/ResidentGroups";
import { PlanType } from "@/commons/services/Plans";
import S3UrlService from "@/commons/services/S3Url";
import SystemError from "@/commons/models/SystemError";
import PublicS3UrlToBase64 from "@/commons/utils/PublicS3UrlToBase64";
import ImgElementToBase64 from "@/commons/utils/ImgElementToBase64";
import S3LocationsService from "@/commons/services/S3Locations";
import { S3LocationRequest, S3LocationResponse, S3LocationType, S3LocationMessage } from "@/commons/models/S3Location";
import S3UploadsService from "@/commons/services/S3Uploads";
import ExistS3FilesService, { ExistS3FilesPostModel } from "@/commons/services/ExistS3Files";

import EventFileCommon from "@/commons/services/EventFileCommon";

import OkCancelModal from "@/pc/basics/OkCancelModal.vue";
import OkModal from "@/pc/basics/OkModal.vue";
import HelpIcon from "@/pc/basics/HelpIcon.vue";
import PageLoading from "@/pc/basics/PageLoading.vue";
import ProgressLoading from "@/pc/basics/ProgressLoading.vue";
import ValidationAlert from "@/pc/basics/ValidationAlertByValidator.vue";
import FormInput from "@/pc/components/forms/FormInput.vue";
import FormTextarea from "@/pc/components/forms/FormTextarea.vue";
import FormFile from "@/pc/components/forms/FormFile.vue";
import FormInputEventTargetType from "@/pc/components/forms/FormInputEventTargetType.vue";
import FormCheck from "@/pc/components/forms/FormCheck.vue";
import FormInputEventHoldType from "@/pc/components/forms/FormInputEventHoldType.vue";
import EventTargetSelectModal, { EventTargetSelectModalModel } from "@/pc/components/modals/EventTargetSelectModal.vue";
import EventPreview from "@/pc/basics/EventPreview.vue";
import FormEventDatetimePicker from "@/pc/components/forms/FormEventDatetimePicker.vue";
import FormDatePicker from "@/pc/components/forms/FormDatePicker.vue";
import FormMultiFile from "@/pc/containers/events/FormMultiFile.vue";
import DraggableFiles, { ExtDraggableFile } from "@/pc/components/ExtDraggableFiles.vue";
import EventFilePreviewModal from "@/pc/containers/events/EventFilePreviewModal.vue";

import eventDefaultImage from "@/assets/event-default.jpg";

/**
 * イベント編集画面コンポーネント
 */
@Component({
  components: {
    HelpIcon,
    OkCancelModal,
    OkModal,
    FormInput,
    FormTextarea,
    FormFile,
    FormInputEventTargetType,
    FormCheck,
    FormInputEventHoldType,
    EventTargetSelectModal,
    EventPreview,
    FormEventDatetimePicker,
    FormDatePicker,
    FormMultiFile,
    DraggableFiles,
    EventFilePreviewModal,
    PageLoading,
    ProgressLoading,
    ValidationAlert
  },
  $_veeValidate: { validator: "new" }
})
export default class EventEdit extends Vue {
  /** イベントサービス */
  @Inject("EventsService") eventsService!: EventsService;
  /** S3Urlサービス */
  @Inject("S3UrlService") s3UrlService!: S3UrlService;
  /** 利用者グループサービス */
  @Inject("ResidentGroupsService") residentGroupsService!: ResidentGroupsService;
  /** S3格納先URLサービス */
  @Inject("S3LocationsService") s3LocationsService!: S3LocationsService;
  /** S3ファイルアップロードサービス */
  @Inject("S3UploadsService") s3UploadsService!: S3UploadsService;
  /** S3ファイル存在確認サービス */
  @Inject("ExistS3FilesService") existS3FilesService!: ExistS3FilesService;

  @Ref()
  filePreviewModal!: EventFilePreviewModal;

  /** 添付許可Content-Typeリスト */
  readonly allowContentType = ["application/pdf"];
  readonly allowMessageType = ["pdf"];
  /** ファイル登録数 */
  readonly registerFileUploadLimit: number = 1;

  /** ページ全体ローディング表示可否 */
  isBusy: boolean = false;
  /** ローディングメッセージ */
  loadingMessage: string = "";
  /** 宛先選択モーダル表示可否 */
  isTargetSelectModal: boolean = false;
  /** 更新確認モーダル表示可否 */
  showUpdateModal: boolean = false;
  /** 更新完了モーダル表示可否 */
  showUpdateSuccessModal: boolean = false;

  /** バリデーションアラート表示可否 */
  showValidationAlert: boolean = false;

  /** プラン種別 */
  planType: PlanType = PlanType.UNKNOWN;

  /** イベント日時 */
  eventDatetime: number | null = null;
  /** 日付 */
  eventDate: string = "";
  /** 開始時刻 */
  eventTimeFrom: string = "";
  /** 終了時刻 */
  eventTimeTo: string = "";
  /** 公開範囲 */
  targetType: EventTargetType | null = null;
  /** 開催方法*/
  holdType: EventHoldType | null = null;
  /** 参加申込設定 */
  needReply: boolean | null = null;
  /** 参加申し込み設定、値変更可否 */
  needReplyDisabled: boolean = false;
  /** 参加申込み締切日 */
  replyDeadline: number = 0;
  /** 参加申込み締切日 */
  replyDeadlineDate: string | null = null;
  /** 場所 */
  place: string = "";
  /** タイトル */
  title: string = "";
  /** 内容 */
  description: string = "";
  /** 実際に登録するファイル */
  files: Array<ExtDraggableFile> = [];
  /** ファイル選択コンポーネントで選択したファイル */
  imageFile: any = null;
  /** ファイル選択コンポーネントで選択したファイルのURL */
  imageUrl: string = "";
  /** ファイル選択コンポーネントで選択したファイル名 */
  imageName: string = "";
  /** 画面表示時に取得したイベント */
  event: EventGetModel | null = null;
  /** イベントの画像が無い場合に表示するデフォルト画像 */
  defaultImage: string = eventDefaultImage;

  /** 宛先選択モーダルのタイトル */
  headerTitle: string = "利用者グループ";
  /** 宛先種別が「利用者グループ」の場合の宛先種別選択肢 */
  residentGroups: Array<EventTargetSelectModalModel> = [];
  /** 宛先 */
  eventTargetSelectModalModel: Array<EventTargetSelectModalModel> = [];
  /** 宛先選択肢(キャンセル等がありえるので一時コピーした宛先を子コンポーネントに渡す) */
  tempEventTargetSelectModalModel: Array<EventTargetSelectModalModel> = [];

  /** エラーメッセージ */
  errorMessage: string = "";

  /** 現在のファイルアップロード数 */
  currentUploadCount: number = 0;
  /** ファイルアップロード総数 */
  uploadCount: number = 0;
  /** プログレスモーダルメッセージ */
  progressMessage: string = "";
  /** プログレスモーダル表示可否 */
  progressLoading: boolean = false;
  /** ファイルアップロードエラーモーダル表示可否 */
  showUploadErrorModal: boolean = false;

  /**
   * ### 開催方法の変更イベント
   *
   * - オンライン開催、現地とオンライン開催の場合は強制的に参加申込設定をtrueにする
   * - 参加申込設定をtrueする場合は値変更不可にする
   */
  @Watch("holdType")
  public changeHoldType(newValue: EventHoldType): void {
    if (newValue == EventHoldType.ONLINE_ONLY || newValue == EventHoldType.LOCAL_AND_ONLINE) {
      this.needReply = true;
      this.needReplyDisabled = true;
    } else {
      this.needReplyDisabled = false;
    }

    return;
  }

  /**
   * FileSelecterコンポーネントにバインドした画像の変更イベント
   * ファイル選択、ファイル削除時に呼ばれる
   */
  @Watch("imageFile")
  public async changeImageFile(newFile: string): Promise<void> {
    if (newFile) {
      this.imageUrl = newFile;
    } else {
      this.imageUrl = "";
    }

    return;
  }

  get _targetEventTargetSelectModalModel(): Array<EventTargetSelectModalModel> {
    let targets: Array<EventTargetSelectModalModel> = [];

    for (const target of this.eventTargetSelectModalModel) {
      if (target.isChecked == true) {
        targets.push(target);
      }
    }

    return targets;
  }

  /**
   * ### 登録可能なファイル数を返す
   */
  get _registerFilesLimit(): number {
    const limit: number = this.registerFileUploadLimit - this.files.length;
    return limit;
  }

  /**
   * ### プレビュー表示可否を返す
   */
  get _enablePreviewStatus(): boolean {
    return 0 < this.files.length ? true : false;
  }

  /**
   * vue lifecycle created
   */
  public created() {
    CustomValidate.addCustomValidate(this.$validator);

    return;
  }

  /**
   * vue lifecycle mounted
   */
  public async mounted(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "読込み中...";

    this.planType = userStore.facility.planType;
    await this.createView();

    this.isBusy = false;

    return;
  }

  /**
   * ### 画面表示情報を作成する
   *
   * - イベント情報を取得する
   *   - 取得したイベント情報は更新時に使用するので保持する
   * - イベント情報から入力要素の初期値を格納する
   * - 利用者グループ情報を取得する
   * - 利用者グループ情報とイベント情報を使用して入力要素の利用者グループの初期値を格納する
   * - 公開範囲が「利用者グループ」の場合は入力要素の利用者グループの情報を公開先として使用する
   */
  public async createView(): Promise<void> {
    const eventId: number = Number(this.$route.params.id);
    const event: EventGetModel = await this.getEvent(eventId);
    this.event = event;

    await this.setInputs(event);

    const groups: Array<ResidentGroupsGetModel> = await this.getResidentGroups();
    this.setResidentGroups(groups, event);

    if (EventTargetType.RESIDENT_GROUP == this.targetType) {
      this.eventTargetSelectModalModel = this.residentGroups;
    } else {
      this.eventTargetSelectModalModel = [];
    }

    return;
  }

  /**
   * ### 編集対象のイベント取得
   */
  public async getEvent(eventId: number): Promise<EventGetModel> {
    const response: EventGetModel = await this.eventsService.get(userStore, eventId);
    return response;
  }

  /**
   * ### 入力要素を格納する
   *
   * - イベント情報から画面の入力要素に値を格納する
   * - 画像が存在する場合は画像を取得し、Base64エンコードして入力要素に格納する
   */
  public async setInputs(event: EventGetModel): Promise<void> {
    const eventDate: string = this.$dateUtil.convertNumberToString(event.date, "yyyy-mm-dd");
    const eventTimeFrom: string = this.$dateUtil.convertNumberToString(event.timeFrom, "hh:MM:ss");
    const eventTimeto: string = this.$dateUtil.convertNumberToString(event.timeTo, "hh:MM:ss");

    let replyDeadlineDate: string = "";
    if (event.replyDeadline != undefined) {
      replyDeadlineDate = this.$dateUtil.convertNumberToString(event.replyDeadline, "yyyy-mm-dd");
    } else {
      replyDeadlineDate = this.$dateUtil.convertDateToString(new Date(), "yyyy-mm-dd");
    }

    this.eventDatetime = event.date;
    this.eventDate = eventDate;
    this.eventTimeFrom = eventTimeFrom;
    this.eventTimeTo = eventTimeto;
    this.targetType = event.targetType;
    this.needReply = event.needReply;
    this.replyDeadline = event.replyDeadline != undefined ? event.replyDeadline : event.date;
    this.replyDeadlineDate = replyDeadlineDate;
    this.holdType = event.holdType;
    this.place = event.place;
    this.title = event.title;
    this.description = event.description;

    this.files = [];
    if (event.attachedFiles && 0 < event.attachedFiles.length) {
      for (const fileUrl of event.attachedFiles) {
        this.files.push({
          name: fileUrl.name,
          type: "existing",
          fileObject: undefined,
          url: fileUrl.url
        });
      }
    }

    if (event.imageUrl) {
      // 画像のURLの末尾がファイル名
      const imagePaths = event.imageUrl.split("/");
      this.imageName = imagePaths[imagePaths.length - 1];
      // 画像URLが存在する場合は画像をBase64エンコードしたものを保持し、表示する
      const imageUrl: string = await this.convertImageUrl(event.imageUrl);
      this.imageFile = await PublicS3UrlToBase64.convert(imageUrl, "image/jpeg");
      this.imageUrl = this.imageFile;
    }

    return;
  }

  /**
   * S3に保存されている画像URLを公開設定にし、公開された画像URLを取得する
   */
  public async convertImageUrl(imageUrl: string): Promise<string> {
    let convertImageUrl: string = "";

    if (imageUrl) {
      // 画像URL取得
      convertImageUrl = await this.s3UrlService.get(userStore, Setting.backets.imageExpireHour, imageUrl);
    }

    return convertImageUrl;
  }

  /**
   * ### 利用者グループを取得する
   */
  public async getResidentGroups(): Promise<Array<ResidentGroupsGetModel>> {
    const groups: Array<ResidentGroupsGetModel> = await this.residentGroupsService.gets(userStore);
    return groups;
  }

  /**
   * ### 宛先の利用者グループを格納する
   *
   * - 利用者グループを取得する
   * - 利用者グループの情報を作成する
   * - 公開範囲が利用者グループの場合、選択されているか否かを復元する
   */
  public setResidentGroups(groups: Array<ResidentGroupsGetModel>, event: EventGetModel): void {
    let residentGroups: Array<EventTargetSelectModalModel> = [];
    for (const group of groups) {
      let isChecked: boolean = false;
      if (EventTargetType.RESIDENT_GROUP == this.targetType) {
        for (const target of event.targets) {
          if (group.id == target) {
            isChecked = true;
          }
        }
      }

      const residentGroup: EventTargetSelectModalModel = {
        name: group.name,
        id: group.id,
        isChecked: isChecked
      };
      residentGroups.push(residentGroup);
    }

    this.residentGroups = residentGroups;

    return;
  }

  /**
   * ### 宛先選択モーダルを表示する
   */
  public showTargetSelectModal(): void {
    switch (this.targetType) {
      case EventTargetType.RESIDENT_GROUP: {
        this.eventTargetSelectModalModel = this.residentGroups;
        break;
      }
      default: {
        this.eventTargetSelectModalModel = [];
      }
    }

    this.tempEventTargetSelectModalModel = JSON.parse(JSON.stringify(this.eventTargetSelectModalModel));
    this.isTargetSelectModal = true;

    return;
  }

  /**
   * ### 宛先選択結果を格納する
   */
  public updateTarget(targets: Array<EventTargetSelectModalModel>): void {
    switch (this.targetType) {
      case EventTargetType.RESIDENT_GROUP: {
        this.residentGroups = targets;
        this.eventTargetSelectModalModel = this.residentGroups;
        break;
      }
      default: {
        this.eventTargetSelectModalModel = [];
      }
    }

    return;
  }

  /**
   * ### 宛先を削除する
   */
  public deleteTarget(target: EventTargetSelectModalModel): void {
    target.isChecked = false;
    return;
  }

  /**
   * ### 子コンポーネントでファイルが選択された場合のイベント処理
   *
   * - ファイルが上限数以上なら無視する
   * - バリデーションを行う
   * - バリデーションエラーでなければ、ファイルを追加する
   */
  public async inputFiles(files: Array<File>): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "読込み中...";

    const fileCommon: EventFileCommon = new EventFileCommon(this.$validator, "tempfiles-multiple-register-errors");
    const addFiles: Array<ExtDraggableFile> = await fileCommon.createAddFile(files, this.registerFileUploadLimit, this.files.length);

    if (0 < addFiles.length) {
      this.files = this.files.concat(addFiles);
      for (let i = 0; i < this.files.length; i++) {
        this.files[i].key = i.toString();
      }
    }

    this.isBusy = false;
    return;
  }

  /**
   * ### 添付ファイルプレビューボタン押下イベント処理
   *
   * - ダイアログで添付ファイルをプレビュー表示する
   */
  public async onTempFilePreview(): Promise<void> {
    // ファイルを取得していないものがあったら取得する
    const files = [];
    for (const file of this.files) {
      this.isBusy = true;
      this.loadingMessage = "読込み中...";

      if (!file.fileObject) {
        file.fileObject = await this.getS3File(file.url as string, file.name);
        files.push(file.fileObject);
      } else {
        files.push(file.fileObject);
      }

      this.isBusy = false;
    }

    // ファイルをモーダルで表示する
    if (this.filePreviewModal && this.filePreviewModal instanceof EventFilePreviewModal) {
      this.filePreviewModal.show("", false, files);
    }

    return;
  }

  /**
   * ### S3に格納されているFileを取得する
   *
   * @param url 元URL
   */
  public async getS3File(url: string, fileName: string): Promise<File> {
    const response: File = await this.s3UrlService.getFile(userStore, Setting.backets.imageExpireHour, url, fileName);
    return response;
  }

  /**
   * ### 指定されたファイルを削除する
   */
  public onFileDelete(index: number): void {
    this.files.splice(index, 1);
    return;
  }

  /**
   * ### 更新確認モーダルを表示する
   *
   * - 表示前にバリデーションチェックして、エラーならモーダルは表示しない
   */
  public async showConfirmUpdateModal(): Promise<void> {
    // チェック結果を初期化してからチェックしないと、エラーメッセージが残り続けてしまう
    this.$validator.errors.remove("event-targets");

    const validationResult: boolean = await this.$validator.validateAll();
    const eventDateValidationResult: boolean = this.validateEventDate(this.eventDatetime);
    const eventTargetsValidationResult: boolean = this.validateEventTarget(this.targetType as EventTargetType, this.eventTargetSelectModalModel);
    const replyDeadlineValidationResult: boolean = this.validationReplyDeadline(this.needReply as boolean, this.replyDeadline, this.eventDatetime);
    const eventTimeValidationResult: boolean = this.validationEventTime(this.eventTimeFrom, this.eventTimeTo, this.holdType as EventHoldType);

    if (
      validationResult == true &&
      eventDateValidationResult == true &&
      eventTargetsValidationResult == true &&
      replyDeadlineValidationResult == true &&
      eventTimeValidationResult == true
    ) {
      this.showValidationAlert = false;
      this.showUpdateModal = true;
    } else {
      this.showValidationAlert = true;
    }

    return;
  }

  /**
   * ### 日付バリデーション
   *
   * - 実際に存在する日付かチェック
   */
  public validateEventDate(eventDate: number | null): boolean {
    if (eventDate == null) {
      return true;
    }

    const existDate = this.$dateUtil.checkExistDate(eventDate);
    return existDate;
  }

  /**
   * ### 宛先バリデーション
   *
   * - 必須入力チェック
   */
  public validateEventTarget(targetType: EventTargetType, targets: Array<EventTargetSelectModalModel>): boolean {
    if (targetType == EventTargetType.ALL) {
      return true;
    }

    for (const target of targets) {
      if (target.isChecked == true) {
        return true;
      }
    }

    this.$validator.errors.add({ field: "event-targets", msg: "公開先は必須項目です" });

    return false;
  }

  /**
   * ### 参加申込み締切日時のバリデーション
   *
   * - 参加申込み有りの場合は必須チェック
   * - 実際に存在する日付かチェック
   */
  public validationReplyDeadline(needRply: boolean, replyDeadline: number, eventDatetime: number | null): boolean {
    let replyDeadlineValidationResult: boolean = true;

    if (needRply == true) {
      if (!replyDeadline) {
        this.$validator.errors.add({ field: "reply-deadline", msg: "参加申込み締切日は必須項目です" });
        replyDeadlineValidationResult = false;
      } else {
        const existDate = this.$dateUtil.checkExistDate(replyDeadline);
        if (existDate == false) {
          this.$validator.errors.add({ field: "reply-deadline", msg: "参加申込み締切日は存在しない年月日です" });
          replyDeadlineValidationResult = false;
        }

        if (eventDatetime != null) {
          const slicedEventDatetime: string = eventDatetime.toString().slice(0, 8);
          const slicedReplyDeadline: string = replyDeadline.toString().slice(0, 8);

          if (Number(slicedEventDatetime) < Number(slicedReplyDeadline)) {
            this.$validator.errors.add({ field: "reply-deadline", msg: "参加申込み締切日が日付より未来です" });
            replyDeadlineValidationResult = false;
          }
        }
      }
    }

    return replyDeadlineValidationResult;
  }

  /**
   * ### イベント時間のバリデーション
   *
   * - 実施種別がオンラインに関わる場合はイベント実施時間は1時間までに制限する
   */
  public validationEventTime(from: string, to: string, holdType: EventHoldType): boolean {
    let validationResult: boolean = true;

    if (holdType == EventHoldType.LOCAL_AND_ONLINE || holdType == EventHoldType.ONLINE_ONLY) {
      const fromHour: number = Number(from.slice(0, 2));
      const fromMin: number = Number(from.slice(3, 5));
      const totalFromMin: number = fromHour * 60 + fromMin;
      const toHour: number = Number(to.slice(0, 2));
      const toMin: number = Number(to.slice(3, 5));
      const totalToMin: number = toHour * 60 + toMin;

      if (60 < totalToMin - totalFromMin) {
        this.$validator.errors.add({ field: "event-date", msg: "オンラインイベントの場合、実施時間は1時間までです" });
        validationResult = false;
      }
    }

    return validationResult;
  }

  /**
   * イベント更新
   */
  public async updateEvent(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "更新中...";

    console.log("start updateEvent()");

    // ファイルが添付されていればファイルのアップロード
    let fileUrls: Array<FileUrl> = [];
    // ファイルアップロードするタイミングでファイルが削除（移動含む）、リネームされた場合は失敗するのでここでtry-catchする
    // アップロードに失敗した場合は添付されたファイルを全てリセットしてモーダルで再添付を促す
    try {
      console.log("start uploadFiles()");
      console.log("this.files", this.files);

      if (0 < this.files.length) {
        const onlyExist = this.files.every((file) => file.type === "existing");
        // 全て既存ファイルの場合は既存情報をそのまま使用する
        if (onlyExist) {
          for (const file of this.files) {
            fileUrls.push({
              name: file.name,
              url: file.url as string
            });
          }
        }
        // 新規ファイルが含まれる場合はアップロードする
        else {
          const targetFiles = [];
          for (const file of this.files) {
            if (!file.fileObject) {
              file.fileObject = await this.getS3File(file.url as string, file.name);
            }
            targetFiles.push(file.fileObject as File);
          }
          const urls: Array<S3LocationResponse> = await this.createUploadUrls(targetFiles, S3LocationType.EVENT);
          fileUrls = await this.uploadFiles(urls);
        }
      }
    } catch (error) {
      this.isBusy = false;

      this.showUploadErrorModal = true;
      this.files = [];

      return;
    }

    console.log("start createRequestBody()");

    try {
      const requestBody: EventPatchModel = this.createRequestBody(fileUrls);
      await this.eventsService.patch(userStore, Number(this.$route.params.id), requestBody);

      // イベント一覧を再読み込みさせるようstoreを更新
      let eventList: EventListState = needRefreshesStore.eventList;
      eventList.eventList = true;
      needRefreshesStore.setEventList(eventList);

      // 更新成功ダイアログを表示する
      this.showUpdateSuccessModal = true;
    } catch (error) {
      const systemError: SystemError = error as SystemError;
      // ステータスコードが409の場合、他端末にて更新が行われているので、画面情報を再取得してから再入力を促す
      if (systemError.statusCode == 409) {
        await this.createView();
        this.errorMessage = "他の端末で更新されたか、表示している情報が古いために更新できませんでした。情報を再読み込みして、もう一度お試しください。";
      } else {
        throw error;
      }
    } finally {
      this.isBusy = false;
    }

    return;
  }

  /**
   * ### ファイルアップロード先のURLを発行する
   */
  public async createUploadUrls(files: Array<File>, s3LocationType: S3LocationType): Promise<Array<S3LocationResponse>> {
    let extensions: Array<string> = [];
    for (const file of files) {
      const _file: Array<string> = file.name.split("/");
      const _extensions: Array<string> = _file[_file.length - 1].split(".");
      extensions.push("." + _extensions[_extensions.length - 1]);
    }
    const params: S3LocationMessage = {
      userId: userStore.loginUser.userId
    };
    const requestBody: S3LocationRequest = {
      type: s3LocationType,
      count: files.length,
      extensions: extensions,
      params: params,
      acl: "public-read"
    };
    const url: Array<S3LocationResponse> = await this.s3LocationsService.post(userStore, requestBody);

    return url;
  }

  /**
   * ### プログレスローディングを表示してファイルアップロードする
   */
  public async uploadFiles(urls: Array<S3LocationResponse>): Promise<Array<FileUrl>> {
    const fileUrls: Array<FileUrl> = [];

    try {
      // プログレス付きモーダルを表示する
      // 進捗はファイルのアプロード状況を反映させるようにする
      this.currentUploadCount = 0;
      this.uploadCount = this.files.length;
      this.progressMessage = "ファイルアップロード中。\n完了するまでそのままお待ちください。\n別画面に遷移しますと処理がキャンセルされます。";
      this.progressLoading = true;
      // アップロード
      for (let i = 0; i < urls.length; i++) {
        this.currentUploadCount = i;
        await this.s3UploadsService.uploadFile(urls[i], this.files[i].fileObject as File);

        // DBにはファイルではなく、添付ファイルのS3URLを保持するためURL配列を作成する
        const preUrl: string = urls[i].url.endsWith("/") ? urls[i].url : `${urls[i].url}/`;
        const s3Url: string = `${preUrl}${urls[i].fields["key"]}`;

        fileUrls.push({
          name: this.files[i].name,
          url: s3Url
        });
      }
      // アップロードしたファイルはサムネイル画像が作成されたり、movからmp4への変換が行われる
      // ファイルの存在確認をして変換されたか確認する
      await this.existFile(fileUrls);
    } finally {
      this.progressLoading = false;
    }

    return fileUrls;
  }

  /**
   * ### ファイルの存在確認を行う
   */
  public async existFile(fileUrls: Array<FileUrl>): Promise<void> {
    // ファイルがアップロードされているかをチェックする
    const s3Urls: string[] = [];
    for (const fileUrl of fileUrls) {
      s3Urls.push(fileUrl.url);
    }
    const fileRequestBody: ExistS3FilesPostModel = { urls: s3Urls };
    await this.existS3FilesService.post(userStore, fileRequestBody);

    // サムネイルのファイルが存在しているかをチェックする
    let thumbnailUrls: Array<string> = [];
    for (const s3Url of s3Urls) {
      // サムネイルのファイル名は<元ファイル名>_thumbnailになる
      const splitedUrl: string[] = s3Url.split("/");
      const splitedFileName: string[] = splitedUrl[splitedUrl.length - 1].split(".");

      // mov、mp4以外の場合はサムネイルをメッセージ送信時に作成するのでスキップ
      if (splitedFileName[1] != "mov" && splitedFileName[1] != "mp4") {
        continue;
      }

      const thumbnailFileName: string = splitedFileName[0] + "_thumbnail.jpeg";
      let thunmbnailUrl: string = "";
      for (let i = 0; i < splitedUrl.length - 1; i++) {
        thunmbnailUrl += splitedUrl[i] + "/";
      }
      thunmbnailUrl += thumbnailFileName;
      thumbnailUrls.push(thunmbnailUrl);
    }

    if (0 < thumbnailUrls.length) {
      const thumbnailRequestBody: ExistS3FilesPostModel = { urls: thumbnailUrls };
      await this.existS3FilesService.post(userStore, thumbnailRequestBody);
    }

    return;
  }

  /**
   * ### イベント更新パラメータ作成
   *
   * - 画像をBase64エンコードした情報を作成する
   *   - 画像が指定されていない場合はデフォルト画像を使用する
   * - イベント日時はイベント日時を変換したものを使用する
   * - 宛先種別が「全て」以外(利用者グループ以上)の場合は宛先を作成する
   * - 作成した情報からリクエストボディを作成する
   */
  public createRequestBody(attachedFiles: Array<FileUrl>): EventPatchModel {
    if (!this.imageUrl) {
      const img: any = this.$refs.defaultImage;
      this.imageUrl = ImgElementToBase64.convert(img, "image/jpeg");
    }

    const eventDate: string = this.$dateUtil.replaceNonNumeric(this.eventDate);
    const event: EventGetModel = this.event as EventGetModel;

    let targets: number[] = [];
    if (this.targetType != null && EventTargetType.RESIDENT_GROUP <= this.targetType) {
      for (const target of this.eventTargetSelectModalModel) {
        if (target.isChecked == true) {
          targets.push(target.id);
        }
      }
    }

    let replyDeadline: number | undefined = undefined;
    if (this.needReply == true) {
      const slicedReplyDeadline: string = this.replyDeadline.toString().slice(0, 8);
      replyDeadline = Number(`${slicedReplyDeadline}235959000`);
    }

    let meetingToken: string = "";
    if (this.holdType == EventHoldType.LOCAL_AND_ONLINE || this.holdType == EventHoldType.ONLINE_ONLY) {
      meetingToken = uuid.v4();
    }

    const requestBody: EventPatchModel = {
      isPublic: event.isPublic,
      date: Number(this.eventDatetime),
      timeFrom: Number(`${eventDate}${this.convertEventTime(this.eventTimeFrom)}`),
      timeTo: Number(`${eventDate}${this.convertEventTime(this.eventTimeTo)}`),
      targetType: this.targetType as EventTargetType,
      targets: targets,
      needReply: this.needReply as boolean,
      replyDeadline: replyDeadline,
      holdType: this.holdType as EventHoldType,
      place: this.$stringUtil.convertYen(this.place),
      title: this.$stringUtil.convertYen(this.title),
      description: this.$stringUtil.convertYen(this.description),
      attachedFiles: attachedFiles,
      image: this.imageUrl,
      meetingToken: meetingToken,
      writtenBy: userStore.loginUser.name,
      writerId: userStore.loginUser.userId,
      updated: event.updated
    };

    return requestBody;
  }

  /**
   * 文字列の時刻(hh:MM)を数値の日付(yyymmddhhMMssSSS)に変換する
   * - 時刻は時分の形式(例 00:20)なので、「:」を削除して、秒の部分を加える
   *   timepickerコンポーネントの仕様により、3桁(8:30等)がありえるので、3桁なら先頭を0埋めする
   *   ※分は2桁が約束されているので、1桁かどうかを意識するのは時間だけ
   */
  public convertEventTime(time: string): string {
    const nonNumeriTime: string = this.$dateUtil.replaceNonNumeric(time);
    return `${nonNumeriTime}000`;
  }

  /**
   * イベント一覧に遷移する
   */
  public gotoEventList(): void {
    this.$router.push(PcUrl.EventList);

    return;
  }
}
</script>

<style lang="scss" scoped>
@import "../../../styles/pc/_colors.scss";

#event-edit {
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;

  .update-btn,
  .cancel-btn {
    width: 120px;
  }

  .target-select-btn {
    margin: 0 8px 4px 0;
  }
  .event-target {
    display: inline-block;
    padding: 7px;
    font-size: 16px;
    margin: 0 8px 4px 0;
    border-radius: 3px;
    transition: all 0.2s;
    border: 1px solid $form-border-color;

    .target-name {
      margin: 0 8px 0 0 !important;
    }
    .delete-btn {
      margin: 0 !important;
      font-size: 14px !important;
    }
  }
}
</style>
